import type { DetailedViolation, ViolationGroupInfo } from "./types";
import { getViolationRange } from "./violationRange";

function clampGroupToZone(
  type: string,
  startDay: number,
  endDay: number,
  dayIndex: number,
  pattern?: string,
  tailStart = 0,
  daysInMonth = 0
): ViolationGroupInfo | null {
  const clampedGlobalStart = Math.max(startDay, -tailStart);
  const clampedGlobalEnd = Math.min(endDay, daysInMonth - 1);

  if (clampedGlobalEnd < clampedGlobalStart) return null;

  const rangeStart = clampedGlobalStart;
  const rangeEnd = clampedGlobalEnd;

  if ((rangeStart >= 0 && dayIndex >= 0) || (rangeEnd < 0 && dayIndex < 0)) {
    return {
      type,
      pattern,
      startDay: rangeStart,
      endDay: rangeEnd,
      rangeLength: rangeEnd - rangeStart + 1,
      isStart: dayIndex === rangeStart,
      isEnd: dayIndex === rangeEnd,
    };
  }

  if (dayIndex >= 0) {
    const clampedStart = Math.max(rangeStart, 0);
    const startedInTail = rangeStart < 0;
    return {
      type,
      pattern,
      startDay: clampedStart,
      endDay: rangeEnd,
      rangeLength: rangeEnd - clampedStart + 1,
      isStart: !startedInTail && dayIndex === clampedStart,
      isEnd: dayIndex === rangeEnd,
    };
  }

  return {
    type,
    pattern,
    startDay: rangeStart,
    endDay,
    rangeLength: endDay - rangeStart + 1,
    isStart: dayIndex === rangeStart,
    isEnd: dayIndex === endDay,
  };
}

export function buildViolationGroup(
  violation: DetailedViolation,
  dayIndex: number,
  groupTotal?: number,
  required?: number,
  tailStart = 0,
  daysInMonth = 0
): ViolationGroupInfo | null {
  switch (violation.type) {
    case "manpower_shortage":
      if (
        violation.day === dayIndex &&
        groupTotal !== undefined &&
        required !== undefined &&
        groupTotal < required
      ) {
        return {
          type: violation.type,
          startDay: dayIndex,
          endDay: dayIndex,
          rangeLength: 1,
          isStart: true,
          isEnd: true,
        };
      }
      return null;

    case "manpower_excess":
      if (
        violation.day === dayIndex &&
        groupTotal !== undefined &&
        violation.required_max !== undefined &&
        groupTotal > violation.required_max
      ) {
        return {
          type: violation.type,
          startDay: dayIndex,
          endDay: dayIndex,
          rangeLength: 1,
          isStart: true,
          isEnd: true,
        };
      }
      return null;

    case "banned_day_after_eve":
      if (violation.day === dayIndex || violation.next_day === dayIndex) {
        return clampGroupToZone(
          violation.type,
          violation.day ?? dayIndex,
          violation.next_day ?? dayIndex,
          dayIndex,
          violation.pattern,
          tailStart,
          daysInMonth
        );
      }
      return null;

    case "max_conseq_work":
    case "three_seq_nig": {
      const range = getViolationRange(violation);
      if (range && dayIndex >= range.startDay && dayIndex <= range.endDay) {
        return clampGroupToZone(
          violation.type,
          range.startDay,
          range.endDay,
          dayIndex,
          undefined,
          tailStart,
          daysInMonth
        );
      }
      return null;
    }

    case "two_offs_after_two_nig":
    case "two_offs_after_three_nig": {
      const range = getViolationRange(violation);
      if (range && dayIndex >= range.startDay && dayIndex <= range.endDay) {
        return clampGroupToZone(
          violation.type,
          range.startDay,
          range.endDay,
          dayIndex,
          undefined,
          tailStart,
          daysInMonth
        );
      }
      return null;
    }

    default:
      return null;
  }
}
